import type { ReactNode } from 'react';
import { css, styled } from 'styled-components';

import { Button } from '#/components/Button/Button';
import { StyledVersionedRouterLink } from '#/components/Link/StyledVersionedRouterLink';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useOrgDashboardOrgContext } from '#/modules/orgDashboard/contexts/OrgDashboardOrgContext';
import { useHandleBuy } from '#/modules/orgDashboard/utils/orgDashboardMemberships';
import { colors } from '#/theme/theme';
import { getStaticImageSrc } from '#/utils/getImageSrc';

const BannerIcon = styled.img`
  margin-top: 4px;
  width: 38px;
  height: 38px;
  margin-right: 32px;
`;

const Banner = styled.div<{
  $renewal: boolean;
}>`
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 16px;

  border: 2px solid transparent;

  ${({ $renewal }: { $renewal: boolean }) =>
    $renewal
      ? css`
          background:
            linear-gradient(${colors.lightYellow}, ${colors.lightYellow})
              padding-box,
            linear-gradient(
                285deg,
                ${colors.brandBlue},
                ${colors.brandGreen},
                ${colors.brandYellow}
              )
              border-box;
        `
      : css`
          background:
            linear-gradient(${colors.lightSage}, ${colors.lightSage})
              padding-box,
            linear-gradient(
                285deg,
                ${colors.brandYellow},
                ${colors.brandGreen},
                ${colors.brandBlue}
              )
              border-box;
        `}
`;

const BannerLeft = styled.div`
  display: flex;
  flex: 1;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BannerTextHeading = styled.div`
  font-weight: 600;
`;

type Props = {
  canRenew: boolean | null;
  expirationDate: Date | null;
  isExpired: boolean | null;
  setError: (message: string) => void;
  linkToMembership?: boolean;
};

export function OrgDashboardMembershipCallout({
  canRenew,
  expirationDate,
  isExpired,
  setError,
  linkToMembership,
}: Props) {
  const { i18n } = useI18n();

  const { org } = useOrgDashboardOrgContext();

  const handleBuy = useHandleBuy({
    canRenew,
    setError,
  });

  const link = <StyledVersionedRouterLink to={`/admin/${org.id}/membership`} />;

  if (canRenew && expirationDate) {
    let bannerText: ReactNode;

    if (isExpired) {
      if (linkToMembership) {
        bannerText = i18n(
          'Renew today to enjoy a host of membership {{link}}benefits{{/link}}',
          {
            link,
          },
        );
      } else {
        bannerText = i18n('Renew today to enjoy a host of membership benefits');
      }
    } else {
      if (linkToMembership) {
        bannerText = i18n(
          'Renew today to enjoy a host of membership {{link}}benefits{{/link}}',
          {
            link,
          },
        );
      } else {
        bannerText = i18n('Renew today to enjoy a host of membership benefits');
      }
    }
    return (
      <Banner $renewal>
        <BannerLeft>
          <BannerIcon src={getStaticImageSrc('ding.svg')} alt="Ding Icon" />
          <BannerText>
            <BannerTextHeading>
              {isExpired
                ? i18n('Your Membership is Expired')
                : i18n('Your Membership is Expiring Soon')}
            </BannerTextHeading>
            <div>{bannerText}</div>
          </BannerText>
        </BannerLeft>
        <Button
          qaId="org-dashboard-renew-membership-button"
          type="button"
          variant="primary"
          onClick={handleBuy}
        >
          {i18n('Renew')}
        </Button>
      </Banner>
    );
  }
  if (!expirationDate) {
    return (
      <Banner $renewal={false}>
        <BannerLeft>
          <BannerIcon
            src={getStaticImageSrc('action-green.svg')}
            alt="Action icon"
          />
          <BannerText>
            <BannerTextHeading>{i18n('Become a Member')}</BannerTextHeading>
            <div>
              {linkToMembership
                ? i18n(
                    'Enjoy a host of {{link}}benefits{{/link}} when you become a member for $149 /year',
                    {
                      link,
                    },
                  )
                : i18n(
                    'Enjoy a host of benefits when you become a member for $149 /year',
                  )}
            </div>
          </BannerText>
        </BannerLeft>
        <Button
          qaId="org-dashboard-buy-membership-button"
          type="button"
          variant="primary"
          onClick={handleBuy}
        >
          {i18n('Join')}
        </Button>
      </Banner>
    );
  }
  return null;
}
